import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Prop,
  State,
} from "@stencil/core";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import { navContentStore } from "@phn/state/nav-content-store";
import { findTopLevelRoutingKey } from "@phn/entities/routing-key";
import { componentsOnReady } from "@phn/utility/ready";
import { Timeout } from "@phn/utility/timeout";
import { back, reset } from "../../services/routing-manager";
import { isMobile, isString } from "@phn/utility/helper";
import { getHeadlineText } from "../../services/menu-items-service";
import analyticsManager from "../../services/analytics/analytics-manager";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";

@Component({
  tag: "phn-nd-drawer-header",
  styleUrl: "drawer-header.scss",
  scoped: true,
})
export class DrawerHeader {
  @Prop() backButtonText = "";

  @Prop() backButtonAnalyticsId!: string;

  @Prop() showBackButton = false;

  @Prop() route!: string;

  @Prop() breakpoint!: number;

  @Prop() receivedContent = false;

  @Prop() locale?: string;

  @Prop() activeRoutingKey?: string = "";

  @State() showSkeleton = true;

  @Event() clickDrawerHeaderClose!: EventEmitter<void>;

  @Element() el!: HTMLPhnNdDrawerHeaderElement;

  componentWillLoad(): void {
    this.waitForComponentsToLoad();
  }

  private async waitForComponentsToLoad() {
    await componentsOnReady(this.el, new Timeout(5000));
    this.showSkeleton = !this.receivedContent;
  }

  private sendDataLayerEvent() {
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.MENU_CLOSE_CLICK,
      {
        componentClick: {
          clickElementId: "mainmenu.drawer.close",
          clickElementName: "X",
          clickElementType: ClickElementTypes.Button,
          targetElement: "level-0",
        },
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            this.route,
            ElementTypes.closeButton,
          ),
          displayElementName: "X",
        },
      },
    );
  }

  render(): HTMLPhnNdDrawerHeaderElement {
    const topLevelRoutingKey = findTopLevelRoutingKey(this.route);
    const displayHeadlineContent =
      topLevelRoutingKey !== undefined
        ? getHeadlineText(navContentStore.state, this.route, topLevelRoutingKey)
        : "";
    const modelType = displayHeadlineContent.includes("mainmenu.")
      ? displayHeadlineContent.split("mainmenu.")[1]
      : "";

    return (
      <Fragment>
        <div class="drawer-header">
          {this.showBackButton ? (
            <Fragment>
              <phn-nd-back-button
                class="back-button"
                onClick={() => {
                  back();
                }}
                analyticsId={this.backButtonAnalyticsId}
                text={isMobile(this.breakpoint) ? "" : this.backButtonText}
                style={!this.showSkeleton ? {} : { display: "none" }}
              />
              {this.showSkeleton ? <div class="button-skeleton" /> : null}
            </Fragment>
          ) : (
            <div class="spacer" />
          )}
          {isMobile(this.breakpoint) ? (
            modelType === "" ? (
              <Fragment>
                {this.showSkeleton && this.showBackButton ? (
                  <div class="skeleton-headline"></div>
                ) : null}
                <phn-nd-heading
                  text={displayHeadlineContent}
                  style={
                    this.showSkeleton || !isString(displayHeadlineContent)
                      ? { display: "none" }
                      : {}
                  }
                  class="heading-items"
                />
              </Fragment>
            ) : (
              <Fragment>
                <phn-nd-signature-heading text={modelType} />
              </Fragment>
            )
          ) : null}

          <Fragment>
            <phn-p-button-pure
              class="close-button"
              icon="close"
              hideLabel="true"
              aria={{ "aria-label": "close dialog" }}
              size="small"
              style={!this.showSkeleton ? {} : { display: "none" }}
              data-test-id="close-button"
              onClick={() => {
                this.sendDataLayerEvent();
                reset();
              }}
            >
              Close Dialog
            </phn-p-button-pure>
            {this.showSkeleton ? <div class="button-skeleton" /> : null}
          </Fragment>
        </div>
      </Fragment>
    ) as HTMLPhnNdDrawerHeaderElement;
  }
}
